import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { ApolloError } from 'apollo-client';

import { Typography } from 'src/typography';
import { PayByBankLogo } from 'src/assets/pay-by-bank-logo';
import LoadingSpinner from 'src/assets/loader/spinner';
import { PhoneBankGraphic } from 'src/assets/phone-bank-graphic';
import { ErrorContent } from 'src/components/modals/kiosk-checkout/v2/error-content';

import { INSTORE_EVENTS, useDutchiePayAnalytics } from 'src/payments/hooks';

type PbbQrCodeProps = {
  instoreCartUrl: string | null;
  loading: boolean;
  error?: ApolloError;
  handleOnErrorClose: () => void;
};

export const PbbQrCode = ({ instoreCartUrl, loading, error, handleOnErrorClose }: PbbQrCodeProps): JSX.Element => {
  if (error) {
    return <ErrorContent onClose={handleOnErrorClose} />;
  }

  return (
    <>
      <Content>
        <ContentContainer>
          <ContentHeading size='large' tag='h1'>
            Scan to pay <br /> with your bank login
          </ContentHeading>
          <PhoneBankGraphic />
        </ContentContainer>
        <QrCodeContainer>
          {instoreCartUrl && !loading ? (
            <QRCode instoreCartUrl={instoreCartUrl} />
          ) : (
            <Loader data-testid='loading-indicator'>
              <LoadingSpinner size={100} borderColor='#f3f6f8' />
            </Loader>
          )}
        </QrCodeContainer>
      </Content>

      <FooterContainer>
        <PayByBankContainer>
          <PayByBankIcon width='70' height='13' />
        </PayByBankContainer>
      </FooterContainer>
    </>
  );
};

const QRCode = ({ instoreCartUrl }: { instoreCartUrl: string }): JSX.Element => {
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();
  useEffect(() => {
    try {
      const url = new URL(instoreCartUrl);
      const checkoutToken = url.searchParams.get('checkoutToken');

      if (checkoutToken) {
        dutchiePayEventTracker(INSTORE_EVENTS.KIOSK_QR_CODE_MODAL_VIEWED, { checkoutToken });
      }
    } catch {
      // Invalid URL, don't track
    }
  }, [instoreCartUrl, dutchiePayEventTracker]);
  return <QRCodeSVG data-testid='qr-code' value={instoreCartUrl} size={265} />;
};

const Content = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    align-items: center;
    justify-content: space-around;
    display: flex;
    height: 344px;
    min-width: 640px;
    background: ${colors.white};
    border-radius: 30px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: ${spaces[3]} ${spaces[8]} 0;
  `}
`;

const ContentContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
`;

const ContentHeading = styled(Typography.Heading)`
  color: ${({ theme }) => theme.colors.primaryBlack};
  font-size: 24px;
  font-weight: 700;
  line-height: 30px;
`;

const QrCodeContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spaces[1]};
    justify-content: center;
    margin: ${spaces[6]} 0;
    min-height: 265px;
    min-width: 265px;
    border: 2px solid ${colors.grey[90]};
    border-radius: 16px;
    padding: ${spaces[2]};
    color: ${colors.grey[50]};

    p {
      text-transform: uppercase;
    }
  `}
`;

const FooterContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    background-color: ${colors.green10};
    border-radius: 0 0 30px 30px;
    padding: ${spaces[4]} ${spaces[6]} ${spaces[4]} ${spaces[6]};
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 640px;
    height: 46px;
    font-size: 13px;
    color: ${colors.grey[50]};
  `}
`;

const PayByBankContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spaces[1]};
  justify-content: center;
`;

const PayByBankIcon = styled(PayByBankLogo)`
  margin-top: 1.25px;
`;

const Loader = styled.div`
  align-items: center;
  border-radius: 100px;
  display: flex;
  height: 100px;
  justify-content: center;
  width: 100px;
`;
